import React from 'react';
import { IconButton, Stack, Switch, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import moment from 'moment';
import { MUITableColumn } from 'components/shared/MUITable';
import { CreditSubmissionDocumentRow, SubmissionDocumentRaw, SubmissionDocumentRow } from '../types';
import { DateFormats } from 'constants/common';

export const capitalize = (str: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '');

export const mapToDecisionLetterRow = (doc: SubmissionDocumentRaw): SubmissionDocumentRow => ({
  id: doc.documentId,
  date: doc.createdDateTime ? moment(doc.createdDateTime).format(DateFormats.Short) : '-',
  uploadedBy: doc.createdBy ?? '-',
  type: capitalize(doc.docType) || '-',
  description: doc.docDescription ?? '-',
  source: doc.source,
});

export const mapToCreditSubDocRow = (
  doc: SubmissionDocumentRaw,
  creditSubmissionId?: string
): CreditSubmissionDocumentRow => {
  let lenderAccess = false;

  if (doc.documentCreditSubmission) {
    const match = doc.documentCreditSubmission.find(s => s.creditSubmissionId === creditSubmissionId);
    lenderAccess = match?.lenderAccess ?? false;
  }

  return {
    id: doc.documentId,
    date: doc.createdDateTime ? moment(doc.createdDateTime).format(DateFormats.Short) : '-',
    uploadedBy: doc.createdBy ?? '-',
    type: capitalize(doc.docType) || '-',
    description: doc.docDescription ?? '-',
    source: doc.source,
    lenderAccess: lenderAccess ? 'true' : 'false',
  };
};

export const handleDocumentView = (documentId: string) => {
  const currentPath = window.location.pathname + window.location.search + '#submissions';
  window.open(`/document/${documentId}/${btoa(currentPath)}`, '_blank', 'noreferrer');
};

export const handleDocumentDownload = (source: string) => {
  window.open(source, '_blank', 'noreferrer');
};

const ActionsCell = ({ source, documentId }: { source: string | null; documentId: string }) =>
  source ? (
    <Stack direction="row" spacing={0.5}>
      <IconButton
        size="small"
        color="primary"
        sx={{ border: '1px solid', borderColor: 'rgba(33, 150, 243, 0.5)', width: 32, height: 32, borderRadius: '4px' }}
        onClick={() => handleDocumentDownload(source)}
      >
        <GetAppIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="primary"
        sx={{ border: '1px solid', borderColor: 'rgba(33, 150, 243, 0.5)', width: 32, height: 32, borderRadius: '4px' }}
        onClick={() => handleDocumentView(documentId)}
      >
        <RemoveRedEyeIcon fontSize="small" />
      </IconButton>
    </Stack>
  ) : null;

const headerCell = { sx: { backgroundColor: 'transparent' } };

export const createDecisionLetterColumns = (): MUITableColumn<SubmissionDocumentRow>[] => [
  { header: 'Uploaded On', renderKey: 'date', headerCell },
  { header: 'Uploaded By', renderKey: 'uploadedBy', headerCell },
  { header: 'Type', renderKey: 'type', headerCell },
  { header: 'Description', renderKey: 'description', headerCell },
  {
    header: 'Actions',
    renderKey: 'source',
    headerCell,
    renderCell: (row: SubmissionDocumentRow) => <ActionsCell source={row.source} documentId={row.id} />,
  },
];

export const createSubmissionDocumentsColumns = (
  onToggleLenderAccess: (documentId: string) => void
): MUITableColumn<CreditSubmissionDocumentRow>[] => [
  { header: 'Uploaded On', renderKey: 'date', headerCell },
  { header: 'Uploaded By', renderKey: 'uploadedBy', headerCell },
  { header: 'Type', renderKey: 'type', headerCell },
  { header: 'Description', renderKey: 'description', headerCell },
  {
    header: 'Lender Access',
    renderKey: 'lenderAccess',
    headerCell,
    renderCell: (row: CreditSubmissionDocumentRow) => (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Switch
          size="small"
          color="primary"
          checked={row.lenderAccess === 'true'}
          onChange={() => onToggleLenderAccess(row.id)}
        />
        <Typography variant="body2">Allow</Typography>
      </Stack>
    ),
  },
  {
    header: 'Actions',
    renderKey: 'source',
    headerCell,
    renderCell: (row: CreditSubmissionDocumentRow) => <ActionsCell source={row.source} documentId={row.id} />,
  },
];
